import React, { useState, useContext, useRef, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Dialog } from 'functional-foundations'
import { Typography, Button } from 'foundations-library/components'
import Picture from 'picture'
import { NavigationContext } from 'navigation'
import { useAccountSecurityContext, PASSWORD_EXPIRY_POPUP_SUPPRESSION_KEY } from 'providers'
import API from 'users-app/src/util/api_v5'
import { getRemoteValue } from '../utils'
import { password_lock, password_lock_inline } from '../graphics'

const jss = () => ({
    root: {
        '& .dialog-container': {
            padding: '0'
        },
        '& .dialog-content': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '80px'
        }
    },
    content: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    picture: {
        width: '100%',
        '& img': {
            width: '100%',
            borderRadius: '20px'
        }
    },
    typographyContainer: {
        display: 'flex',
        paddingBottom: '30px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        alignSelf: 'stretch'
    },
    buttonContainer: {
        display: 'flex',
        padding: '30px 0px 40px 0px',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '20px',
        alignSelf: 'stretch',
        borderRadius: '0px 0px 20px 20px'
    }
})

const useStyles = makeStyles(jss)

/**
 *
 */
const PasswordExpiry = ({ hasExpired = true, date, onSessionDismiss }: { hasExpired?: boolean, date?: string, onSessionDismiss?: () => void }) => {
    const classes = useStyles()
    const [isOpen, setIsOpen] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const dialogRef = useRef<HTMLDivElement>(null)
    const { goToNavItem } = useContext(NavigationContext)
    const { passwordSecurityState } = useAccountSecurityContext()

    const texts = hasExpired ? getRemoteValue('password_expired') : getRemoteValue('password_to_expire')

    useEffect(() => {
        let handleKeyDown: (e: KeyboardEvent) => void
        if (isOpen && dialogRef.current) {
            const focusableElements = dialogRef.current.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            const firstElement = focusableElements[0] as HTMLElement
            const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

            handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Tab') {
                    if (document.activeElement === firstElement) {
                        e.preventDefault()
                        lastElement.focus()
                    } else if (document.activeElement === lastElement) {
                        e.preventDefault()
                        firstElement.focus()
                    }
                }
                if (e.key === 'Escape') {
                    handleRemindMeLater()
                }
            }

            firstElement?.focus()
            document.addEventListener('keydown', handleKeyDown)
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [isOpen])

    const getExpireAt = (passwordExpiryDate: string, minusDays: number) => {
        const passwordExpiryDateObj = new Date(passwordExpiryDate)
        passwordExpiryDateObj.setDate(passwordExpiryDateObj.getDate() - minusDays)
        return passwordExpiryDateObj.toISOString()
    }

    const handleClose = () => {
        setIsOpen(false)
    }

    const handleRemindMeLater = () => {
        onSessionDismiss?.()

        const { daysRemainedForPasswordExpiry, passwordExpiryDate } = passwordSecurityState.data
        if (daysRemainedForPasswordExpiry! <= 5) {
            handleClose()
            return
        }

        setIsLoading(true)

        let expire_at = ''
        // call API to update the password expiry dialog shown to true
        if ((daysRemainedForPasswordExpiry! <= 10 && daysRemainedForPasswordExpiry! > 5)) {
            expire_at = getExpireAt(passwordExpiryDate!, 5)
        }
        if ((daysRemainedForPasswordExpiry! <= 15 && daysRemainedForPasswordExpiry! > 10)) {
            expire_at = getExpireAt(passwordExpiryDate!, 10)
        }

        const payload = {
            reminder_key: PASSWORD_EXPIRY_POPUP_SUPPRESSION_KEY,
            expire_at,
            description: 'Password expiry popup suppression'
        }
        API.supressData(payload).catch((error: any) => {
            console.error('----- supressData error -----', error)
        }).finally(() => {
            handleClose()
            setIsLoading(false)
        })
    }

    const handleUpdatePassword = () => {
        goToNavItem('passwords')
        setIsOpen(false)
    }

    return (
        <Dialog className={classes.root} isOpen={isOpen} data-testid={'password-expiry-dialog'}>
            <div ref={dialogRef} className={classes.content} role='dialog' aria-labelledby={'password-expiry-title'} aria-describedby={'password-expiry-description'} aria-modal="true">
                <Picture sources={[{ src: password_lock, type: 'image/jpeg' }]} thumbnail={password_lock_inline} alt='Password Lock graphic' attributes={{ className: classes.picture }} />
                <div className={classes.typographyContainer}>
                    <Typography id='password-expiry-title' variant='h6' color='neutral-900'>{texts.title}</Typography>
                    <Typography id='password-expiry-description' variant='body2' color='neutral-900' align='center'>
                        <span dangerouslySetInnerHTML={{ __html: (texts.subTitle as string).replace('{DATE}', `<strong>${new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>`) }} /> {/* eslint-disable-line @typescript-eslint/naming-convention */}
                    </Typography>
                </div>
                <div className={classes.buttonContainer}>
                    {
                        texts.buttons.secondary &&
                        <Button loading={isLoading} size='large' color='neutral' variant='contained' onClick={handleRemindMeLater}>{texts.buttons.secondary}</Button>
                    }
                    {
                        texts.buttons.primary &&
                        <Button size='large' color='primary' variant='contained' icon={'go'} onClick={handleUpdatePassword}>{texts.buttons.primary}</Button>

                    }
                </div>
            </div>
        </Dialog>
    )
}

export default PasswordExpiry
